import React, { useContext, useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { PBXCallState } from 'app/store/contexts';
import PBXDialerDialog from './PBXDialerDialog';
import PBXTransferAgentsPanel from './PBXTransferAgentsPanel';
import ModalOmniNew from 'allGlobalComponents/ModalOmniNew';
import ButtonOmni from 'allGlobalComponents/ButtonOmni';
import FormFieldOmni from 'allGlobalComponents/FormFieldOmni';
import DeleteIcon from '@mui/icons-material/Delete';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { useApi, useSnackbar } from 'app/hooks';
import MenuItem from '@material-ui/core/MenuItem';
import { SUPERVISE_PANEL_TITLES, AGENT_STATUS_ON_CALL } from './pbxConstants';

const useStyles = makeStyles(theme => ({
    wrapper: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        padding: '10px',
        width: '100%',
        height: '100vh',
        maxHeight: '100vh',
        overflow: 'hidden',
        boxSizing: 'border-box',
    },
    formContent: {
        padding: theme.spacing(3),
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
    },
    section: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1),
    },
    sectionHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    label: {
        fontSize: '12px',
        fontWeight: 600,
        color: theme.palette.text.secondary,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
    },
    input: {
        width: '100%',
        padding: '10px 12px',
        borderRadius: '8px',
        border: `1px solid ${theme.palette.divider}`,
        background: theme.palette.background.paper,
        color: theme.palette.text.primary,
        fontSize: '14px',
        outline: 'none',
        boxSizing: 'border-box',
        transition: 'border-color 0.2s ease',
        '&:focus': {
            borderColor: theme.palette.secondary.value || theme.palette.primary.main,
        },
    },
    inputDisabled: {
        opacity: 0.6,
        cursor: 'not-allowed',
    },
    inputError: {
        borderColor: '#f44336 !important',
    },
    errorText: {
        fontSize: '11px',
        color: '#f44336',
        marginTop: '-4px',
    },
    attrRow: {
        display: 'flex',
        gap: '8px',
        alignItems: 'center',
    },
    attrInput: {
        flex: 1,
        padding: '10px 12px',
        borderRadius: '8px',
        border: `1px solid ${theme.palette.divider}`,
        background: theme.palette.background.paper,
        color: theme.palette.text.primary,
        fontSize: '14px',
        outline: 'none',
        boxSizing: 'border-box',
    },
    modalActions: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: theme.spacing(1),
        width: '100%',
    },
    textarea: {
        width: '100%',
        padding: '10px 12px',
        borderRadius: '8px',
        border: `1px solid ${theme.palette.divider}`,
        background: theme.palette.background.paper,
        color: theme.palette.text.primary,
        fontSize: '14px',
        outline: 'none',
        boxSizing: 'border-box',
        fontFamily: 'inherit',
        resize: 'vertical',
        minHeight: '80px',
        transition: 'border-color 0.2s ease',
        '&:focus': {
            borderColor: theme.palette.secondary.value || theme.palette.primary.main,
        },
    },
}));

function PBXCalls() {
    const classes = useStyles();
    const theme = useTheme();
    const {
        customerInfo,
        updateCustomerInfo,
        accountInfo,
        pbxCallIdRef,
        callState,
        transferCall,
        isTransferring,
        presenceAgents,
        presenceLoading,
        superviseMode,
        closeSuperviseAgentPicker,
        dialSupervise,
    } = useContext(PBXCallState);
    const { request } = useApi();
    const { snackbar, showSuccess, showError } = useSnackbar();

    // =================== Call Transfer ===================
    const transferableAgents = (presenceAgents || []).filter(
        (agent) => agent.extension !== accountInfo?.extension
    );

    const handleTransferAgentClick = (agent) => {
        Swal.fire({
            title: 'Transfer Call?',
            text: `Transfer this call to ${agent.displayName} (ext. ${agent.extension})?`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Transfer',
            cancelButtonText: 'Cancel',
            backdrop: 'rgba(0,0,0,0.2)',
            heightAuto: false,
        }).then((result) => {
            if (result.isConfirmed) {
                transferCall(agent.extension, agent.displayName);
            }
        });
    };

    // =================== Supervisor Monitor / Coaching / Conference ===================
    // Same presence list the transfer panel uses, but only an agent who is
    // actually mid-call is a valid Monitor/Coaching/Conference target —
    // there is nothing to listen in on otherwise.
    const superviseAgents = (presenceAgents || []).filter(
        (agent) => agent.extension !== accountInfo?.extension && agent.status === AGENT_STATUS_ON_CALL
    );

    const handleSuperviseAgentClick = (agent) => {
        dialSupervise(agent);
    };

    // =================== Update Customer Modal ===================
    const [showUpdateModal, setShowUpdateModal] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [name, setName] = useState('');
    const [attrs, setAttrs] = useState([]);
    const [errors, setErrors] = useState({});
    // Snapshot of customer info captured when the modal opens, so saving
    // still works if the call disconnects mid-form (handleCallEnd clears customerInfo).
    const [updateSnapshot, setUpdateSnapshot] = useState(null);

    // =================== Create Ticket Modal ===================
    const [showTicketModal, setShowTicketModal] = useState(false);
    const [isCreatingTicket, setIsCreatingTicket] = useState(false);
    const [ticketStatuses, setTicketStatuses] = useState([]);
    const [ticketForm, setTicketForm] = useState({
        subject: '',
        description: '',
        statusId: '',
    });
    const [ticketErrors, setTicketErrors] = useState({});
    // Snapshot captured when the ticket modal opens — preserves customer + call IDs
    // so the ticket can be created and linked even if the call drops before submit.
    const [ticketSnapshot, setTicketSnapshot] = useState(null);

    // =================== Fetch ticket statuses ===================
    const fetchTicketStatuses = () => {
        request({
            module: 'pbx',
            method: 'pbxTicketStatusList',
            payload: {},
            onSuccess: (response) => {
                const list = response?.data?.data?.data || [];
                setTicketStatuses(list);
                // Auto-select first status if available
                if (list.length > 0 && !ticketForm.statusId) {
                    setTicketForm(prev => ({ ...prev, statusId: String(list[0].id) }));
                }
            },
            onError: () => { },
        });
    };

    // =================== Update Customer Handlers ===================
    const openUpdateModal = () => {
        setName(customerInfo?.name || '');
        const parsed = (customerInfo?.attributes || []).map(attr => {
            if (attr.attribute_name) {
                return { key: attr.attribute_name, value: attr.attribute_value || '' };
            }
            const entries = Object.entries(attr).filter(([key]) => key !== 'attribute_id');
            if (entries.length === 0) return null;
            const [key, value] = entries[0];
            return { key, value };
        }).filter(Boolean);
        setAttrs(parsed);
        setErrors({});
        setUpdateSnapshot({
            customerId: customerInfo?.customerId,
            phoneNumber: customerInfo?.phoneNumber,
        });
        setShowUpdateModal(true);
    };

    const closeUpdateModal = () => {
        if (!isSaving) {
            setShowUpdateModal(false);
            setAttrs([]);
            setErrors({});
            setUpdateSnapshot(null);
        }
    };

    const addAttribute = () => {
        setAttrs(prev => [...prev, { key: '', value: '' }]);
    };

    const updateAttr = (index, field, val) => {
        setAttrs(prev => {
            const updated = [...prev];
            updated[index] = { ...updated[index], [field]: val };
            return updated;
        });
        setErrors(prev => {
            const updated = { ...prev };
            delete updated[`attr_${index}_${field}`];
            return updated;
        });
    };

    const removeAttr = (index) => {
        setAttrs(prev => prev.filter((_, i) => i !== index));
    };

    const validateUpdate = () => {
        const newErrors = {};
        if (!name.trim()) newErrors.name = 'Name is required';

        attrs.forEach((attr, i) => {
            if (!attr.key.trim()) newErrors[`attr_${i}_key`] = 'Required';
            if (!attr.value.trim()) newErrors[`attr_${i}_value`] = 'Required';

            const prevKeys = attrs.slice(0, i).map(a => a.key.trim().toLowerCase());
            if (attr.key.trim() && prevKeys.includes(attr.key.trim().toLowerCase())) {
                newErrors[`attr_${i}_key`] = 'Duplicate key';
            }
        });

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSaveUpdate = () => {
        if (!validateUpdate()) return;

        const customerId = updateSnapshot?.customerId || customerInfo?.customerId;
        if (!customerId) {
            showError('Customer reference lost. Please reopen this dialog.');
            return;
        }

        setIsSaving(true);
        const attributes = attrs
            .filter(a => a.key.trim() && a.value.trim())
            .map(a => ({ [a.key.trim()]: a.value.trim() }));

        updateCustomerInfo(
            customerId,
            name.trim(),
            attributes,
            () => {
                setIsSaving(false);
                setShowUpdateModal(false);
                setUpdateSnapshot(null);
            },
            () => {
                setIsSaving(false);
            }
        );
    };

    // =================== Create Ticket Handlers ===================
    const openTicketModal = () => {
        setTicketForm({ subject: '', description: '', statusId: '' });
        setTicketErrors({});
        fetchTicketStatuses();
        setTicketSnapshot({
            customerId: customerInfo?.customerId,
            customerName: customerInfo?.name,
            phoneNumber: customerInfo?.phoneNumber,
            callId: pbxCallIdRef?.current,
        });
        setShowTicketModal(true);
    };

    const closeTicketModal = () => {
        if (!isCreatingTicket) {
            setShowTicketModal(false);
            setTicketForm({ subject: '', description: '', statusId: '' });
            setTicketErrors({});
            setTicketSnapshot(null);
        }
    };

    const handleTicketFormChange = (field, value) => {
        setTicketForm(prev => ({ ...prev, [field]: value }));
        if (ticketErrors[field]) {
            setTicketErrors(prev => ({ ...prev, [field]: undefined }));
        }
    };

    const validateTicket = () => {
        const newErrors = {};
        if (!ticketForm.subject.trim()) newErrors.subject = 'Subject is required';
        if (!ticketForm.statusId) newErrors.statusId = 'Status is required';
        setTicketErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleCreateTicket = () => {
        if (!validateTicket()) return;

        const customerId = ticketSnapshot?.customerId || customerInfo?.customerId;
        if (!customerId) {
            showError('Customer reference lost. Please reopen this dialog.');
            return;
        }

        setIsCreatingTicket(true);

        const userData = JSON.parse(localStorage.getItem('user_data') || '{}');

        const payload = {
            pbx_customer_id: String(customerId),
            status_id: String(ticketForm.statusId),
            user_id: String(userData?.id || accountInfo?.userId || ''),
            subject: ticketForm.subject.trim(),
            description: ticketForm.description.trim(),
        };

        request({
            module: 'pbx',
            method: 'pbxTicketCreate',
            payload,
            onSuccess: (response) => {
                const ticket = response?.data?.data?.ticket;
                const ticketId = ticket?.id;
                // Prefer the call ID captured when the modal opened — current ref
                // is cleared by handleCallEnd if the call dropped mid-form.
                const callId = ticketSnapshot?.callId || pbxCallIdRef?.current;

                // Link call to ticket if both IDs exist
                if (ticketId && callId) {
                    request({
                        module: 'pbx',
                        method: 'pbxTicketCallsLink',
                        payload: {
                            ticket_id: Number(ticketId),
                            call_id: String(callId),
                        },
                        onSuccess: () => {
                            setIsCreatingTicket(false);
                            setShowTicketModal(false);
                            setTicketForm({ subject: '', description: '', statusId: '' });
                            setTicketSnapshot(null);
                            showSuccess(`Ticket ${ticket?.ticket_number || ''} created & linked to call`);
                        },
                        onError: (linkError) => {
                            // Ticket created but link failed
                            setIsCreatingTicket(false);
                            setShowTicketModal(false);
                            setTicketForm({ subject: '', description: '', statusId: '' });
                            setTicketSnapshot(null);
                            showSuccess(`Ticket ${ticket?.ticket_number || ''} created (call link failed)`);
                        },
                    });
                } else {
                    // No call ID available, just close
                    setIsCreatingTicket(false);
                    setShowTicketModal(false);
                    setTicketForm({ subject: '', description: '', statusId: '' });
                    setTicketSnapshot(null);
                    showSuccess(`Ticket ${ticket?.ticket_number || ''} created successfully`);
                }
            },
            onError: (message) => {
                setIsCreatingTicket(false);
                showError(message || 'Failed to create ticket');
            },
        });
    };

    // =================== Update Modal Content ===================
    const updateModalContent = (
        <div className={classes.formContent}>
            <div className={classes.section}>
                <span className={classes.label}>Phone Number</span>
                <input
                    className={`${classes.input} ${classes.inputDisabled}`}
                    value={customerInfo?.phoneNumber || updateSnapshot?.phoneNumber || ''}
                    readOnly
                />
            </div>

            <div className={classes.section}>
                <span className={classes.label}>Name *</span>
                <input
                    className={`${classes.input} ${errors.name ? classes.inputError : ''}`}
                    value={name}
                    onChange={(e) => {
                        setName(e.target.value);
                        if (errors.name) setErrors(prev => ({ ...prev, name: undefined }));
                    }}
                    placeholder="Enter name"
                />
                {errors.name && <span className={classes.errorText}>{errors.name}</span>}
            </div>

            <div className={classes.section}>
                <div className={classes.sectionHeader}>
                    <span className={classes.label}>Attributes</span>
                    <ButtonOmni onClick={addAttribute} variant="outlined" label="+ Add" size="small" />
                </div>
                {attrs.map((attr, index) => (
                    <div key={index}>
                        <div className={classes.attrRow}>
                            <input
                                className={`${classes.attrInput} ${errors[`attr_${index}_key`] ? classes.inputError : ''}`}
                                value={attr.key}
                                onChange={(e) => updateAttr(index, 'key', e.target.value)}
                                placeholder="Key (e.g. city)"
                            />
                            <input
                                className={`${classes.attrInput} ${errors[`attr_${index}_value`] ? classes.inputError : ''}`}
                                value={attr.value}
                                onChange={(e) => updateAttr(index, 'value', e.target.value)}
                                placeholder="Value (e.g. Karachi)"
                            />
                            <DeleteIcon
                                fontSize="large"
                                style={{ cursor: 'pointer', color: theme.palette.secondary.value, flexShrink: 0 }}
                                onClick={() => removeAttr(index)}
                            />
                        </div>
                        {(errors[`attr_${index}_key`] || errors[`attr_${index}_value`]) && (
                            <div style={{ display: 'flex', gap: '8px', paddingLeft: '2px', marginTop: '2px' }}>
                                <span className={classes.errorText} style={{ flex: 1 }}>{errors[`attr_${index}_key`] || ''}</span>
                                <span className={classes.errorText} style={{ flex: 1 }}>{errors[`attr_${index}_value`] || ''}</span>
                                <span style={{ width: '24px' }} />
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );

    const updateModalActions = (
        <div className={classes.modalActions}>
            <ButtonOmni onClick={closeUpdateModal} variant="outlined" label="Cancel" disabled={isSaving} />
            <ButtonOmni
                onClick={handleSaveUpdate}
                variant="outlined"
                label={isSaving ? 'Saving...' : 'Save'}
                disabled={isSaving || !name.trim()}
            />
        </div>
    );

    // =================== Ticket Modal Content ===================
    const ticketModalContent = (
        <div className={classes.formContent}>
            <div className={classes.section}>
                <span className={classes.label}>Customer</span>
                <input
                    className={`${classes.input} ${classes.inputDisabled}`}
                    value={
                        customerInfo?.name
                        || customerInfo?.phoneNumber
                        || ticketSnapshot?.customerName
                        || ticketSnapshot?.phoneNumber
                        || ''
                    }
                    readOnly
                />
            </div>

            <div className={classes.section}>
                <span className={classes.label}>Subject *</span>
                <input
                    className={`${classes.input} ${ticketErrors.subject ? classes.inputError : ''}`}
                    value={ticketForm.subject}
                    onChange={(e) => handleTicketFormChange('subject', e.target.value)}
                    placeholder="Enter ticket subject"
                />
                {ticketErrors.subject && <span className={classes.errorText}>{ticketErrors.subject}</span>}
            </div>

            <div className={classes.section}>
                <span className={classes.label}>Status *</span>
                <FormFieldOmni
                    type="select"
                    variant="outlined"
                    size="small"
                    value={ticketForm.statusId}
                    onChange={(e) => handleTicketFormChange('statusId', e.target.value)}
                    error={ticketErrors.statusId}
                    placeholder="Select status"
                    options={ticketStatuses.map((status) => ({
                        value: String(status.id),
                        label: status.label || status.name,
                    }))}
                />
            </div>
            <div className={classes.section}>
                <span className={classes.label}>Description</span>
                <textarea
                    className={classes.textarea}
                    value={ticketForm.description}
                    onChange={(e) => handleTicketFormChange('description', e.target.value)}
                    placeholder="Enter ticket description (optional)"
                    rows={3}
                />
            </div>
        </div>
    );

    const ticketModalActions = (
        <div className={classes.modalActions}>
            <ButtonOmni onClick={closeTicketModal} variant="outlined" label="Cancel" disabled={isCreatingTicket} />
            <ButtonOmni
                onClick={handleCreateTicket}
                variant="outlined"
                label={isCreatingTicket ? 'Creating...' : 'Create Ticket'}
                disabled={isCreatingTicket || !ticketForm.subject.trim() || !ticketForm.statusId}
            />
        </div>
    );

    return (
        <div className={classes.wrapper}>
            {callState === 'active' && (
                <PBXTransferAgentsPanel
                    agents={transferableAgents}
                    loading={presenceLoading}
                    transferring={isTransferring}
                    onTransfer={handleTransferAgentClick}
                />
            )}

            {callState === 'idle' && superviseMode && (
                <PBXTransferAgentsPanel
                    agents={superviseAgents}
                    loading={presenceLoading}
                    transferring={false}
                    onTransfer={handleSuperviseAgentClick}
                    title={SUPERVISE_PANEL_TITLES[superviseMode]}
                    actionHint={`Select an agent currently on a call to ${superviseMode}`}
                    actionVerb="Dial"
                    onClose={closeSuperviseAgentPicker}
                    selectableStatus={AGENT_STATUS_ON_CALL}
                    emptyText="No agents currently on a call."
                />
            )}

            <PBXDialerDialog
                onUpdateInfoClick={openUpdateModal}
                onCreateTicketClick={openTicketModal}
            />

            {/* Update Customer Modal */}
            <ModalOmniNew
                open={showUpdateModal}
                handleClose={closeUpdateModal}
                title="Update Customer Info"
                content={updateModalContent}
                actions={updateModalActions}
                customWidth={500}
                disableBackdropClick={isSaving}
                disableEscapeKeyDown={isSaving}
            />

            {/* Create Ticket Modal */}
            <ModalOmniNew
                open={showTicketModal}
                handleClose={closeTicketModal}
                title="Create Ticket"
                content={ticketModalContent}
                actions={ticketModalActions}
                customWidth={500}
                disableBackdropClick={isCreatingTicket}
                disableEscapeKeyDown={isCreatingTicket}
            />
        </div>
    );
}

export default PBXCalls;